import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  Link,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Download,
  Email,
  FormatQuote,
  Business,
  Launch,
  Info,
} from '@mui/icons-material';

const PressPage = () => {
  const theme = useTheme();

  const logos = [
    {
      name: 'PWGen-Rust Logo (Wide - Light)',
      file: '/assets/images/PWGenLogo-Wide.png',
      description: 'Horizontal logo for light backgrounds',
      format: 'PNG',
      size: '2000x600',
    },
    {
      name: 'PWGen-Rust Logo (Wide - Dark)',
      file: '/assets/images/PWGenLogo-Dark-Wide.png',
      description: 'Horizontal logo for dark backgrounds',
      format: 'PNG',
      size: '2000x600',
    },
    {
      name: 'PWGen-Rust Icon',
      file: '/logo192.png',
      description: 'Square icon for app stores and social media',
      format: 'PNG',
      size: '192x192',
    },
    {
      name: 'Kief Studio Logo (Light)',
      file: '/img/KS-Logo-Full-Light.png',
      description: 'Kief Studio logo for dark backgrounds',
      format: 'PNG',
      size: '800x200',
    },
    {
      name: 'Kief Studio Logo (Dark)',
      file: '/img/KS-Logo-Full-Dark.png',
      description: 'Kief Studio logo for light backgrounds',
      format: 'PNG',
      size: '800x200',
    },
  ];

  const pwgenBrandColors = [
    { name: 'Primary Blue', hex: '#1976d2', rgb: 'rgb(25, 118, 210)' },
    { name: 'Accent Green', hex: '#00695c', rgb: 'rgb(0, 105, 92)' },
    { name: 'Aqua', hex: '#26a69a', rgb: 'rgb(38, 166, 154)' },
    { name: 'Dark Background', hex: '#121212', rgb: 'rgb(18, 18, 18)' },
  ];

  const kiefStudioBrandColors = [
    { name: 'Purple Primary', hex: '#BD00E2', rgb: 'rgb(189, 0, 226)' },
    { name: 'Black', hex: '#0D0D0D', rgb: 'rgb(13, 13, 13)' },
    { name: 'Yellow Accent', hex: '#EDC903', rgb: 'rgb(237, 201, 3)' },
    { name: 'White', hex: '#FFFFFF', rgb: 'rgb(255, 255, 255)' },
    { name: 'Blue', hex: '#085294', rgb: 'rgb(8, 82, 148)' },
  ];

  const factSheet = [
    { label: 'Product Name', value: 'PWGen-Rust' },
    { label: 'Company', value: 'Kief Studio LLC' },
    { label: 'Founded', value: '2022' },
    { label: 'License', value: 'Apache 2.0 (Core), Commercial (Enterprise)' },
    { label: 'Platform', value: 'Windows, macOS, Linux' },
    { label: 'Technology', value: 'Rust, Zero-Knowledge Architecture' },
    { label: 'Encryption', value: 'AES-256-GCM + Argon2' },
    { label: 'Latest Version', value: 'v1.2.0' },
  ];

  const quotes = [
    {
      quote: "PWGen-Rust focuses on making your vault data fortress-strong. With military-grade encryption and zero-knowledge architecture, your stored credentials remain secure even in worst-case scenarios.",
      attribution: "Security Philosophy",
    },
    {
      quote: "While passwords remain a primary attack vector, PWGen-Rust ensures that once your data is in the vault, it's protected by industry-leading encryption that would take centuries to crack.",
      attribution: "Technical Overview",
    },
    {
      quote: "Built with Rust's memory safety and zero-knowledge principles, PWGen-Rust ensures your vault data remains private and secure, accessible only to you.",
      attribution: "Product Focus",
    },
  ];

  return (
    <Box sx={{ py: { xs: 8, md: 12 }, minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          {/* Kief Studio Logo */}
          <Box
            component="img"
            src={theme.palette.mode === 'dark' 
              ? "/img/KS-Logo-Full-Light.png" 
              : "/img/KS-Logo-Full-Dark.png"
            }
            alt="Kief Studio"
            sx={{
              height: { xs: 50, sm: 60 },
              width: 'auto',
              mb: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          />
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Press & Media Kit
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}
          >
            Everything you need to write about PWGen-Rust and Kief Studio
          </Typography>
        </Box>

        {/* Contact Alert */}
        <Alert 
          severity="info" 
          icon={<Business />}
          sx={{ mb: 6, borderRadius: 2 }}
        >
          <Typography variant="body1">
            For press inquiries, interviews, or additional resources, please contact{' '}
            <Link 
              href="https://kief.studio/contactus" 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ fontWeight: 600 }}
            >
              Kief Studio
            </Link>
            , the company behind PWGen-Rust.
          </Typography>
        </Alert>


        {/* Fact Sheet */}
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
          Fact Sheet
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 6,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Grid container spacing={3}>
            {factSheet.map((fact, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {fact.label}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {fact.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Logos */}
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
          Logos & Assets
        </Typography>
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {logos.map((logo, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    background: (index === 1 || index === 3) ? '#1a1a1a' : '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 150,
                  }}
                >
                  <Box
                    component="img"
                    src={logo.file}
                    alt={logo.name}
                    sx={{
                      maxWidth: '80%',
                      height: 'auto',
                      maxHeight: 80,
                    }}
                  />
                </Box>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {logo.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {logo.description}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                    <Chip label={logo.format} size="small" />
                    <Chip label={logo.size} size="small" variant="outlined" />
                  </Stack>
                  <Button
                    startIcon={<Download />}
                    size="small"
                    sx={{ mt: 2 }}
                    fullWidth
                    variant="outlined"
                    href={logo.file}
                    download={`${logo.name.replace(/\s+/g, '_')}.${logo.format.toLowerCase()}`}
                    component="a"
                  >
                    Download
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Brand Identity Cards */}
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
          Brand Identity
        </Typography>
        
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {/* PWGen-Rust Software Brand Card */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                border: '2px solid',
                borderColor: '#1976d2',
                borderRadius: 3,
                height: '100%',
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(0, 105, 92, 0.05))'
                  : 'linear-gradient(135deg, rgba(25, 118, 210, 0.05), rgba(0, 105, 92, 0.02))',
              }}
            >
              <Stack spacing={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    component="img"
                    src={theme.palette.mode === 'dark' 
                      ? "/assets/images/PWGenLogo-Dark-Wide.png" 
                      : "/assets/images/PWGenLogo-Wide.png"
                    }
                    alt="PWGen-Rust"
                    sx={{
                      height: 60,
                      width: 'auto',
                      mb: 2,
                    }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2' }}>
                    PWGen-Rust
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Password Management Software
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  A secure, open-source password manager built with Rust technology, featuring zero-knowledge architecture and military-grade encryption.
                </Typography>
                
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Product Brand Colors
                  </Typography>
                  <Grid container spacing={2}>
                    {pwgenBrandColors.map((color, index) => (
                      <Grid size={6} key={index}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box
                            sx={{
                              width: '100%',
                              height: 50,
                              backgroundColor: color.hex,
                              borderRadius: 1,
                              mb: 1,
                              boxShadow: 1,
                              border: color.hex === '#FFFFFF' ? '1px solid #e0e0e0' : 'none',
                            }}
                          />
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            {color.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {color.hex}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Kief Studio Company Brand Card */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                border: '2px solid',
                borderColor: '#BD00E2',
                borderRadius: 3,
                height: '100%',
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(135deg, rgba(189, 0, 226, 0.1), rgba(237, 201, 3, 0.05))'
                  : 'linear-gradient(135deg, rgba(189, 0, 226, 0.05), rgba(237, 201, 3, 0.02))',
              }}
            >
              <Stack spacing={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    component="img"
                    src={theme.palette.mode === 'dark' 
                      ? "/img/KS-Logo-Full-Light.png" 
                      : "/img/KS-Logo-Full-Dark.png"
                    }
                    alt="Kief Studio"
                    sx={{
                      height: 60,
                      width: 'auto',
                      mb: 2,
                    }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#BD00E2' }}>
                    Kief Studio LLC
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Software Development Company
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  A security-focused software development company specializing in privacy tools, cybersecurity solutions, and enterprise software development.
                </Typography>
                
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Company Brand Colors
                  </Typography>
                  <Grid container spacing={2}>
                    {kiefStudioBrandColors.map((color, index) => (
                      <Grid size={6} key={index}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box
                            sx={{
                              width: '100%',
                              height: 50,
                              backgroundColor: color.hex,
                              borderRadius: 1,
                              mb: 1,
                              boxShadow: 1,
                              border: color.hex === '#FFFFFF' ? '1px solid #e0e0e0' : 'none',
                            }}
                          />
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            {color.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {color.hex}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Brand Relationship */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 6,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            textAlign: 'center',
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.02)'
              : 'rgba(0, 0, 0, 0.02)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Brand Relationship
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            <strong>Kief Studio LLC</strong> is the software development company that creates and maintains 
            <strong> PWGen-Rust</strong>, a password management application. PWGen-Rust is one of several 
            security-focused software products developed by Kief Studio.
          </Typography>
        </Paper>

        {/* Key Messages */}
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
          Key Messages & Quotes
        </Typography>
        <Stack spacing={3} sx={{ mb: 6 }}>
          {quotes.map((item, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 4,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                borderLeft: '4px solid',
                borderLeftColor: 'primary.main',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <FormatQuote sx={{ color: 'primary.main', fontSize: 32, mt: -1 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic', lineHeight: 1.6 }}>
                    {item.quote}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    — {item.attribution}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>


        {/* Contact CTA */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            border: '2px solid',
            borderColor: theme.palette.accent?.main || 'primary.main',
            borderRadius: 3,
            background: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.05),
          }}
        >
          <Email sx={{ fontSize: 48, color: theme.palette.accent?.main || 'primary.main', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Need More Information?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            For press inquiries, interview requests, or custom assets, please reach out to our team at Kief Studio.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<Launch />}
              href="https://kief.studio/contactus"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ py: 1.5, px: 4 }}
            >
              Contact Kief Studio
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Info />}
              href="https://kief.studio"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ py: 1.5, px: 4 }}
            >
              About Kief Studio
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default PressPage;